import { parseCookies, setCookie, destroyCookie } from 'nookies';

// Cookie key for storing consent preferences
export const COOKIE_CONSENT_KEY = '__savvy_cookie_consent__';

// Cookie categories
export enum CookieCategory {
  NECESSARY = 'necessary',
  ANALYTICS = 'analytics',
  MARKETING = 'marketing',
  PREFERENCES = 'preferences',
}

// Consent preferences interface
export interface CookieConsentPreferences {
  necessary: boolean; // Always true - required for site functionality
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
  consentDate: string;
  consentVersion: string;
}

// Current consent version - increment when cookie policy changes
export const CONSENT_VERSION = '1.0';

// Default preferences (only necessary enabled)
export const DEFAULT_PREFERENCES: CookieConsentPreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
  consentDate: '',
  consentVersion: CONSENT_VERSION,
};

// All accepted preferences
export const ALL_ACCEPTED_PREFERENCES: CookieConsentPreferences = {
  necessary: true,
  analytics: true,
  marketing: true,
  preferences: true,
  consentDate: '',
  consentVersion: CONSENT_VERSION,
};

/**
 * Get stored consent preferences from cookies
 */
export function getStoredConsentPreferences(): CookieConsentPreferences | null {
  const cookies = parseCookies();
  const storedConsent = cookies[COOKIE_CONSENT_KEY];

  if (!storedConsent) {
    return null;
  }

  try {
    const preferences = JSON.parse(storedConsent) as CookieConsentPreferences;

    // Check if consent version matches current version
    if (preferences.consentVersion !== CONSENT_VERSION) {
      // Version mismatch - clear old consent and request new consent
      destroyCookie(null, COOKIE_CONSENT_KEY, { path: '/' });
      return null;
    }

    return preferences;
  } catch {
    return null;
  }
}

/**
 * Check if user has given consent
 */
export function hasUserConsented(): boolean {
  return getStoredConsentPreferences() !== null;
}

/**
 * Save consent preferences to cookies
 */
export function saveConsentPreferences(
  preferences: Omit<CookieConsentPreferences, 'consentDate' | 'consentVersion'>
): void {
  const fullPreferences: CookieConsentPreferences = {
    ...preferences,
    necessary: true, // Always ensure necessary is true
    consentDate: new Date().toISOString(),
    consentVersion: CONSENT_VERSION,
  };

  setCookie(null, COOKIE_CONSENT_KEY, JSON.stringify(fullPreferences), {
    maxAge: 365 * 24 * 60 * 60, // 1 year
    path: '/',
    sameSite: 'lax',
  });
}

/**
 * Accept all cookies
 */
export function acceptAllCookies(): void {
  saveConsentPreferences({
    necessary: true,
    analytics: true,
    marketing: true,
    preferences: true,
  });
}

/**
 * Reject all optional cookies (keep only necessary)
 */
export function rejectAllCookies(): void {
  saveConsentPreferences({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });
}

/**
 * Check if a specific cookie category is allowed
 */
export function isCategoryAllowed(category: CookieCategory): boolean {
  const preferences = getStoredConsentPreferences();

  if (!preferences) {
    // No consent given - only necessary cookies allowed
    return category === CookieCategory.NECESSARY;
  }

  return preferences[category];
}

// Cookie category metadata for display
export interface CookieCategoryInfo {
  id: CookieCategory;
  name: string;
  description: string;
  isRequired: boolean;
}

export const COOKIE_CATEGORIES: CookieCategoryInfo[] = [
  {
    id: CookieCategory.NECESSARY,
    name: 'Essential Cookies',
    description:
      'These cookies are necessary for the website to function and cannot be switched off. They are usually only set in response to actions made by you such as setting your privacy preferences, logging in, or filling in forms.',
    isRequired: true,
  },
  {
    id: CookieCategory.ANALYTICS,
    name: 'Analytics Cookies',
    description:
      'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website and your experience.',
    isRequired: false,
  },
  {
    id: CookieCategory.MARKETING,
    name: 'Marketing Cookies',
    description:
      'These cookies are used to track visitors across websites to display relevant advertisements. They help us measure the effectiveness of our advertising campaigns.',
    isRequired: false,
  },
  {
    id: CookieCategory.PREFERENCES,
    name: 'Preference Cookies',
    description:
      'These cookies enable the website to remember choices you make (such as your preferred language or region) and provide enhanced, more personal features.',
    isRequired: false,
  },
];
